/**
 * The four figures at the top of Hjem (C9 2026-09-21, reworked 2026-09-22 on the owner's brief
 * "Implement the improved admin Home dashboard"). Each figure is one number with a named source,
 * and nothing is compared with something it is not comparable to:
 *
 * - who is at work now is counted from punches, never from the plan, and is not set against the
 *   whole day's roster (the "2 av 5, 40 %" the mockup drew is people now over people all day);
 * - the day's shifts split by planned time into three states that cannot overlap, and an
 *   unassigned shift is said on its own line rather than added as a fourth part;
 * - the week's hours are the plan for the whole week, which is what the whole-week avtalt tid
 *   can be measured against; what has been clocked so far is a separate, named figure.
 */
import type { ApiTaskAssignment, ApiUser, Shift } from '@/api/data';
import { oslo, osloISO, worked } from '@/rules';
import type { BreakPolicy } from '@timefly/core';

/** "God morgen" before 10, "God dag" until 17, "God kveld" after. Oslo time, like everything. */
export function greeting(nowISO: string): string {
  const hh = Number(oslo(nowISO)?.time.slice(0, 2) ?? 12);
  if (hh < 10) return 'God morgen';
  if (hh < 17) return 'God dag';
  return 'God kveld';
}

const DAYS = ['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'søndag'];
const MONTHS = [
  'januar',
  'februar',
  'mars',
  'april',
  'mai',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'desember',
];

/** "Mandag 14. september 2026", the way the mockup writes it. */
export function longDate(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  const day = DAYS[(d.getUTCDay() + 6) % 7] ?? '';
  const name = MONTHS[d.getUTCMonth()] ?? '';
  return `${day.charAt(0).toUpperCase()}${day.slice(1)} ${d.getUTCDate()}. ${name} ${d.getUTCFullYear()}`;
}

const at = (iso: string | null | undefined) => (iso ? Date.parse(iso) : Number.NaN);
const people = (list: Shift[]) => new Set(list.map((s) => s.user_id)).size;

/**
 * Whether a shift belongs to "now" on `date`: it starts that day in Oslo, or it started before
 * and is still planned to run (the night shift from yesterday evening).
 */
const ofNow = (s: Shift, date: string, now: number) =>
  oslo(s.start_time)?.date === date || (at(s.start_time) <= now && at(s.end_time) > now);

export interface Attendance {
  /** distinct people clocked in and not out, on a shift whose planned end has not passed */
  on: number;
  /** of `on`, those with a recorded break open (Dagen counts them as at work, statusOf) */
  onBreak: number;
  /** distinct people still clocked in on a shift that ended by the plan: a forgotten clock-out
   *  or overtime; neither is proof they are here, so they are not in `on` */
  stale: number;
  /** distinct people with any shift starting today */
  scheduled: number;
  /** distinct people whose planned shift covers this minute */
  plannedNow: number;
  /** of `plannedNow`, those clocked in on that shift: the bar on the card, a like-for-like
   *  comparison (now against now), never clocked-in now against everyone on the day's plan */
  plannedNowIn: number;
}
export function attendance(shifts: Shift[], date: string, now: number): Attendance {
  const mine = shifts.filter((s) => s.user_id != null && ofNow(s, date, now));
  const open = mine.filter((s) => s.punch_in_time && !s.punch_out_time && at(s.punch_in_time) <= now);
  const live = open.filter((s) => !(at(s.end_time) < now));
  return {
    on: people(live),
    onBreak: people(live.filter((s) => s.lunch_in_time && !s.lunch_out_time)),
    stale: people(open.filter((s) => at(s.end_time) < now && oslo(s.start_time)?.date === date)),
    scheduled: people(mine.filter((s) => oslo(s.start_time)?.date === date)),
    plannedNow: people(mine.filter((s) => at(s.start_time) <= now && at(s.end_time) > now)),
    plannedNowIn: people(live.filter((s) => at(s.start_time) <= now && at(s.end_time) > now)),
  };
}

export interface DaySplit {
  total: number;
  /** by planned time, and these three add up to `total` */
  running: number;
  later: number;
  done: number;
  /** a separate fact about the same shifts: nobody is assigned. Not a fourth part of the total */
  unassigned: number;
}
/** Today's shifts by the plan: started and not ended, not started, ended. Punches play no part. */
export function daySplit(todays: Shift[], now: number): DaySplit {
  let running = 0;
  let later = 0;
  let done = 0;
  for (const s of todays) {
    if (at(s.start_time) > now) later++;
    else if (at(s.end_time) > now) running++;
    else done++;
  }
  return {
    total: todays.length,
    running,
    later,
    done,
    unassigned: todays.filter((s) => s.user_id == null).length,
  };
}

export type TaskState = 'none' | 'all-done' | 'open';
export interface TaskCount {
  open: number;
  overdue: number;
  total: number;
  state: TaskState;
}
/**
 * Tasks on today's shifts. Overdue means the task carries its own window and that window has
 * closed with the task not done; a task with no window cannot be late, only undone. There is no
 * priority on a task (ApiTaskAssignment), so none is shown.
 */
export function taskCount(todays: Shift[], now: number): TaskCount {
  const all: ApiTaskAssignment[] = todays.flatMap((s) => s.taskAssignments ?? []);
  const open = all.filter((a) => a.status !== 'completed');
  const overdue = todays.flatMap((s) =>
    (s.taskAssignments ?? []).filter((a) => a.status !== 'completed' && taskDue(a, s) < now),
  );
  const state: TaskState = all.length === 0 ? 'none' : open.length === 0 ? 'all-done' : 'open';
  return { open: open.length, overdue: overdue.length, total: all.length, state };
}

/** Planned hours, start to end, of the assigned shifts: the plan, not what was clocked. */
export const plannedHours = (shifts: Shift[]): number =>
  shifts
    .filter((s) => s.user_id != null)
    .reduce((a, s) => a + Math.max(0, (at(s.end_time) - at(s.start_time)) / 3_600_000 || 0), 0);

/**
 * Clocked hours after breaks, by the company's break policy (rules/index.ts worked, the same
 * calculation the payroll report makes). Only shifts with both punches count; null before the
 * policy is known, so nothing is shown as a guess.
 */
export const registeredHours = (shifts: Shift[], policy: BreakPolicy | null): number | null =>
  policy ? shifts.reduce((a, s) => a + (worked(s, policy) ?? 0), 0) : null;

export interface Contracted {
  /** sum of avtalt tid over the people who have a usable one */
  hours: number;
  /** how many people that sum covers */
  counted: number;
  /** active people whose avtalt tid is unset, zero or negative, so they are not in the sum */
  missing: number;
}
/**
 * Avtalt tid across the active roster, which the week's planned hours are measured against
 * (owner decision 2026-09-21, after looking at what competitors do: Quinyx's "nominal hours").
 *
 * The field reaches only a reader with people.manage (backend/models/User.js colleagueView), so
 * the caller asks only for such a reader; for anyone else every person would read as missing.
 * A person with no usable avtalt tid is left out of the sum and counted in `missing`.
 */
export function contractedHours(users: ApiUser[]): Contracted {
  const active = users.filter((u) => u.active_status);
  const ok = active.filter((u) => u.max_hours_per_week != null && u.max_hours_per_week > 0);
  return {
    hours: ok.reduce((a, u) => a + (u.max_hours_per_week ?? 0), 0),
    counted: ok.length,
    missing: active.length - ok.length,
  };
}

/**
 * Bars for the sparkline, each 0 to 1.
 *
 * Measured across the range of the working days rather than from nought, because from nought
 * one busy day flattens the other six into stubs and the week has no shape. The quietest day
 * anybody works stands at just over a third, the busiest fills the bar, and the days in
 * between spread over what is left. A day with no hours at all is nought: a gap in the week is
 * worth seeing as a gap.
 */
export const sparkline = (perDay: number[]): number[] => {
  const worked = perDay.filter((h) => h > 0);
  if (worked.length === 0) return perDay.map(() => 0);
  const hi = Math.max(...worked);
  const lo = Math.min(...worked);
  const span = hi - lo;
  return perDay.map((h) => (h <= 0 ? 0 : span === 0 ? 1 : 0.35 + 0.65 * ((h - lo) / span)));
};

/**
 * When a task's own window closes, or never. The window is a time of day ("14:00:00", the column is
 * a time), on the shift's day in Oslo; parsed as an instant it was NaN, and on Hjem new Date() of it
 * threw "Invalid time value" and took the page down (Masfjordnes hotell, 2026-09-30).
 */
export function taskDue(a: ApiTaskAssignment, sh: Shift): number {
  if (!a.has_time_range || !a.end_time) return Number.POSITIVE_INFINITY;
  if (/^\d{2}:\d{2}/.test(a.end_time)) {
    const day = oslo(sh.start_time)?.date;
    return day ? Date.parse(osloISO(day, a.end_time.slice(0, 5))) : Number.POSITIVE_INFINITY;
  }
  const t = Date.parse(a.end_time);
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}
